import type { Metadata, MetadataRoute } from "next";
import { SITE_NAME, siteUrl } from "@/lib/brand";
import { converterUrl } from "@/lib/converter";
import { getTool, TOOLS, type Tool } from "@/lib/tools";

// With server tools (Phase 5) "no upload" is no longer true for every tool.
export const HOME_TITLE = converterUrl
  ? `${SITE_NAME}: Free PDF & Image Tools Online, No Sign-up`
  : `${SITE_NAME}: Free PDF & Image Tools Online, No Upload`;
export const HOME_DESCRIPTION = converterUrl
  ? "Merge, split, edit, sign, compress and convert PDFs, Word and images. Free, no sign-up, and most tools never upload your files."
  : "Merge, split, edit, sign and convert PDFs. Resize, compress, crop and convert images. Free, no sign-up, and your files never leave your browser.";

/** Title, description, canonical address and share preview for one page.
 *  The layout adds " | PDF & Pixel" to the title unless `absoluteTitle`. */
/** The share picture (app/opengraph-image.png). Pages that set their own
 *  openGraph replace the file-based one, so every page lists it again. */
const SHARE_IMAGE = {
  url: "/opengraph-image.png",
  width: 1200,
  height: 630,
  alt: `${SITE_NAME}: free PDF and image tools that run right in your browser`,
};

export function pageMetadata({
  title,
  description,
  path,
  absoluteTitle = false,
}: {
  title: string;
  description: string;
  path: string;
  absoluteTitle?: boolean;
}): Metadata {
  const shareTitle = absoluteTitle ? title : `${title} | ${SITE_NAME}`;
  // Pages live at /<slug>/ (trailing slash); point search engines straight there.
  path = path.endsWith("/") ? path : `${path}/`;
  return {
    title: absoluteTitle ? { absolute: title } : title,
    description,
    alternates: { canonical: path },
    openGraph: {
      type: "website",
      siteName: SITE_NAME,
      locale: "en_US",
      url: path,
      title: shareTitle,
      description,
      images: [SHARE_IMAGE],
    },
    twitter: {
      card: "summary_large_image",
      title: shareTitle,
      description,
      images: [SHARE_IMAGE],
    },
  };
}

/** Title and description for a tool page, from lib/tools.ts (FR-7, NFR-10). */
export function toolMetadata(slug: string): Metadata {
  const tool = getTool(slug);
  if (!tool) return {};
  return pageMetadata({
    title: tool.seoTitle,
    description: tool.seoDescription,
    path: `/${tool.slug}`,
  });
}

/** Pages that aren't tools (sitemap, footer). */
export const INFO_PAGES = ["about", "privacy", "terms"];

/** Every public page for sitemap.xml. `lastModified(path)` gives the date the
 *  page last changed (search engines use it to decide what to re-read). */
export function sitemapEntries(
  base = siteUrl,
  lastModified: (path: string) => Date | undefined = () => undefined,
): MetadataRoute.Sitemap {
  const entry = (
    path: string,
    changeFrequency: "weekly" | "monthly" | "yearly",
    priority: number,
  ) => {
    const date = lastModified(path);
    return {
      url: `${base}${path}`,
      ...(date && { lastModified: date }),
      changeFrequency,
      priority,
    };
  };
  return [
    entry("/", "weekly", 1),
    ...TOOLS.map((tool) => entry(`/${tool.slug}/`, "monthly", 0.8)),
    ...INFO_PAGES.map((page) => entry(`/${page}/`, "yearly", 0.3)),
  ];
}

/** robots.txt: every page may be crawled; points crawlers to the sitemap. */
export function robotsRules(base = siteUrl): MetadataRoute.Robots {
  return {
    rules: { userAgent: "*", allow: "/" },
    sitemap: `${base}/sitemap.xml`,
  };
}

// Structured data (schema.org JSON-LD) helps Google show rich results.

/** The site itself, for the home page. */
export function siteJsonLd(base = siteUrl) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        "@id": `${base}/#website`,
        name: SITE_NAME,
        url: `${base}/`,
        description: HOME_DESCRIPTION,
      },
      {
        "@type": "Organization",
        "@id": `${base}/#organization`,
        name: SITE_NAME,
        url: `${base}/`,
        // Google wants a raster logo (PNG/JPG, at least 112 px).
        logo: `${base}/icon-512.png`,
      },
    ],
  };
}

/** Questions and answers shown on the page, as an FAQPage. */
export function faqJsonLd(faq: { q: string; a: string }[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faq.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };
}

/** A tool page: a free web app, plus its place in the site (breadcrumbs). */
export function toolJsonLd(tool: Tool, base = siteUrl) {
  const url = `${base}/${tool.slug}/`;
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebApplication",
        name: tool.name,
        url,
        description: tool.seoDescription,
        applicationCategory: "UtilitiesApplication",
        operatingSystem: "Any (runs in the browser)",
        browserRequirements: "Requires JavaScript",
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        publisher: { "@id": `${base}/#organization` },
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: SITE_NAME,
            item: `${base}/`,
          },
          { "@type": "ListItem", position: 2, name: tool.name, item: url },
        ],
      },
    ],
  };
}

/** Text for a <script type="application/ld+json">; "<" is escaped so the
 *  data can never close the script tag early. */
export function jsonLdText(data: unknown): string {
  return JSON.stringify(data).replace(/</g, "\\u003c");
}
